/** Product families subject to FHFA / FHA county loan limits (excludes Non-QM and other specialty programs). */
export const PROGRAM_FAMILIES_WITH_COUNTY_LOAN_LIMITS = Object.freeze(['Conventional', 'FHA', 'VA', 'USDA']);

/** Standard pricing term chips (years). */
export const PRICING_LOAN_TERMS = Object.freeze([10, 15, 20, 25, 30]);

/** Non-QM adds 40yr (IO / DSCR programs). */
export const PRICING_LOAN_TERMS_NON_QM = Object.freeze([40, ...PRICING_LOAN_TERMS]);

export const NON_QM_FAMILY_ID = 'Non-QM';

export function productFamiliesIncludeNonQm(families) {
  return Array.isArray(families) && families.includes(NON_QM_FAMILY_ID);
}

/** Loan-term filter chips to show for the selected product families. */
export function getPricingLoanTermOptions(families) {
  return productFamiliesIncludeNonQm(families) ? [...PRICING_LOAN_TERMS_NON_QM] : [...PRICING_LOAN_TERMS];
}

/**
 * Non-QM pricing should always include 40yr alongside user-selected terms (deduped, descending).
 */
export function mergeTermsForNonQmPricing(terms, families) {
  const t = Array.isArray(terms) ? [...terms] : [];
  if (!productFamiliesIncludeNonQm(families)) return t;
  const merged = t.includes(40) ? t : [...t, 40];
  return [...new Set(merged)].sort((a, b) => b - a);
}

/** True when Non-QM is the only selected product family (import / pricing should set loan to Non-QM). */
export function isNonQmOnlyProductFamilies(families) {
  return Array.isArray(families)
    && families.length === 1
    && families[0] === NON_QM_FAMILY_ID;
}

export function appliesCountyLoanLimit(programFamily) {
  return PROGRAM_FAMILIES_WITH_COUNTY_LOAN_LIMITS.includes(programFamily);
}

/** Map LOS `loan.mortgageType` → pricing engine `productFamilies` id */
export const MORTGAGE_TYPE_TO_PRICING_FAMILY = Object.freeze({
  Conventional: 'Conventional',
  FHA: 'FHA',
  VA: 'VA',
  USDA: 'USDA',
  'Non-QM': 'Non-QM',
  NonQM: 'Non-QM',
  Non_QM: 'Non-QM',
  NONQM: 'Non-QM',
  NON_QM: 'Non-QM',
});

/** Resolve LOS `mortgageType` strings (including variants) to a pricing product family id. */
export function resolveMortgageTypeToPricingFamily(mortgageType) {
  if (mortgageType == null || mortgageType === '') return undefined;
  const raw = String(mortgageType).trim();
  if (MORTGAGE_TYPE_TO_PRICING_FAMILY[raw]) return MORTGAGE_TYPE_TO_PRICING_FAMILY[raw];
  const compact = raw.replace(/[\s_-]/g, '');
  if (/nonqm/i.test(compact) || /^nqm$/i.test(compact)) return 'Non-QM';
  return undefined;
}

// 2026 FHFA standard conforming loan limits (Conventional baseline, non-high-cost areas)
const CONVENTIONAL_STANDARD_LIMITS_2026 = {
  1: 832750,
  2: 1066250,
  3: 1288800,
  4: 1601750,
};

// 2026 FHA standard floor limits (FHA/VA/USDA baseline: 65% of FHFA conforming limit)
const FHA_STANDARD_LIMITS_2026 = {
  1: 541250,
  2: 693050,
  3: 837700,
  4: 1041125,
};

/**
 * Check loan amount against county loan limits for a specific program type.
 *
 * @param {number} baseLoanAmount
 * @param {number} numberOfUnits - 1, 2, 3, or 4
 * @param {string} programFamily - 'Conventional', 'FHA', 'VA', 'USDA', or 'Non-QM'
 * @param {object|null} countyLimit - result from /api/county-loan-limits/{fipsCode}
 * @param {{ countyLimitUnavailable?: boolean, fipsCode?: string|null }} [options]
 * @returns {{ exceedsCountyLimit: boolean, exceedsStandardLimit: boolean, countyLimitAmount: number, standardLimitAmount: number, message: string|null, warningType: string|null, countyLimitUnavailable?: boolean }}
 */
export function checkLoanLimit(baseLoanAmount, numberOfUnits = 1, programFamily = 'Conventional', countyLimit = null, options = {}) {
  if (!appliesCountyLoanLimit(programFamily)) {
    return { exceedsCountyLimit: false, exceedsStandardLimit: false, countyLimitAmount: 0, standardLimitAmount: 0, message: null, warningType: null };
  }

  const { countyLimitUnavailable = false, fipsCode = null } = options;
  const units = Math.min(Math.max(numberOfUnits || 1, 1), 4);
  const isGovernment = ['FHA', 'VA', 'USDA'].includes(programFamily);
  const standardLimits = isGovernment ? FHA_STANDARD_LIMITS_2026 : CONVENTIONAL_STANDARD_LIMITS_2026;
  const standardLimit = standardLimits[units] || standardLimits[1];
  const programLabel = isGovernment ? programFamily : 'Conventional';

  if (countyLimitUnavailable) {
    const fipsLabel = fipsCode ? `FIPS ${fipsCode}` : 'this property county';
    return {
      exceedsCountyLimit: false,
      exceedsStandardLimit: false,
      countyLimitAmount: 0,
      standardLimitAmount: standardLimit,
      message: `County loan limits could not be loaded for ${fipsLabel}. Pricing will proceed without county limit validation — verify high-balance eligibility before locking.`,
      warningType: 'warning',
      countyLimitUnavailable: true,
    };
  }

  // Determine the county-specific limit (may be higher than standard for high-cost areas)
  const unitKey = units === 1 ? 'oneUnitLimit'
    : units === 2 ? 'twoUnitLimit'
    : units === 3 ? 'threeUnitLimit'
    : 'fourUnitLimit';
  let countyLimitAmount = standardLimit;
  if (countyLimit) {
    countyLimitAmount = countyLimit[unitKey] || standardLimit;
  }

  const exceedsCountyLimit = baseLoanAmount > countyLimitAmount;
  const exceedsStandardLimit = baseLoanAmount > standardLimit;

  const rawCounty = countyLimit?.countyName || 'this county';
  const county = rawCounty.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  const state = countyLimit?.stateCode || '';
  const fmtAmt = (v) => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  let message = null;
  let warningType = null; // 'error' | 'warning' | null

  if (exceedsCountyLimit) {
    // Loan exceeds the county limit entirely — this is an error
    message = `Loan amount (${fmtAmt(baseLoanAmount)}) exceeds the ${units}-unit ${programLabel} county loan limit of ${fmtAmt(countyLimitAmount)} for ${county}, ${state}.`;
    warningType = 'error';
  } else if (exceedsStandardLimit && !exceedsCountyLimit) {
    // Loan exceeds standard baseline but within the high-cost area limit — informational warning
    if (isGovernment) {
      message = `Loan amount (${fmtAmt(baseLoanAmount)}) exceeds the ${units}-unit standard ${programLabel} limit of ${fmtAmt(standardLimit)} but is within the high-cost area limit of ${fmtAmt(countyLimitAmount)} for ${county}, ${state}. "High Balance" products have been automatically included in pricing.`;
    } else {
      message = `Loan amount (${fmtAmt(baseLoanAmount)}) exceeds the ${units}-unit standard Conventional limit of ${fmtAmt(standardLimit)} but is within the high-cost area limit of ${fmtAmt(countyLimitAmount)} for ${county}, ${state}. "High Balance" and "Super Conforming" products have been automatically included in pricing.`;
    }
    warningType = 'warning';
  }

  return { exceedsCountyLimit, exceedsStandardLimit, countyLimitAmount, standardLimitAmount: standardLimit, message, warningType };
}

/**
 * Fetch county loan limits for pricing validation. Tracks whether the API was unavailable
 * so callers do not treat the national baseline as the county limit.
 */
export async function fetchCountyLimitsForPricing(fetchCountyLimit, fipsCode, limitFamilies, year = 2026) {
  const countyLimitsCache = {};
  if (!fipsCode || limitFamilies.length === 0) {
    return { countyLimitsCache, countyLimitUnavailable: false };
  }

  const programTypesNeeded = [...new Set(
    limitFamilies.map((f) => (['FHA', 'VA', 'USDA'].includes(f) ? 'FHA' : 'Conventional')),
  )];
  let countyLimitUnavailable = false;

  for (const pt of programTypesNeeded) {
    try {
      countyLimitsCache[pt] = await fetchCountyLimit({ fipsCode, programType: pt, year }).unwrap();
    } catch (e) {
      console.warn(`[pricing] Could not fetch ${pt} county limit for FIPS ${fipsCode}`, e);
      countyLimitUnavailable = true;
    }
  }

  return { countyLimitsCache, countyLimitUnavailable };
}

/**
 * Build per-family loan limit check results for pricing UI.
 */
export function evaluateCountyLoanLimits(baseLoanAmount, numberOfUnits, limitFamilies, countyLimitsCache, countyLimitUnavailable, fipsCode) {
  const perFamilyResults = [];
  let anyExceedsStandard = false;
  let allExceedCounty = true;

  for (const family of limitFamilies) {
    const isGov = ['FHA', 'VA', 'USDA'].includes(family);
    const pt = isGov ? 'FHA' : 'Conventional';
    const countyLimit = countyLimitsCache[pt] || null;
    const limitCheck = checkLoanLimit(
      baseLoanAmount,
      numberOfUnits,
      family,
      countyLimit,
      {
        countyLimitUnavailable: countyLimitUnavailable && !countyLimit,
        fipsCode,
      },
    );
    perFamilyResults.push({ family, ...limitCheck });

    if (limitCheck.exceedsStandardLimit) anyExceedsStandard = true;
    if (!limitCheck.exceedsCountyLimit) allExceedCounty = false;
  }

  return { perFamilyResults, anyExceedsStandard, allExceedCounty };
}

/**
 * Get the FIPS code and number of units from the loan detail.
 */
export function getLoanPropertyInfo(loanDetail) {
  const property = loanDetail?.property || {};
  return {
    fipsCode: property?.address?.fipsCode || null,
    numberOfUnits: property?.numberOfUnits || 1,
  };
}

const FICO_MIN = 300;
const FICO_MAX = 850;

const toFicoInt = (value) => {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.round(n);
};

export const isValidRepresentativeFico = (value) => {
  const n = toFicoInt(value);
  return n != null && n >= FICO_MIN && n <= FICO_MAX;
};

/**
 * Resolve pricing-engine `loan.representativeFico` (300–850).
 * Mirrors pro-api LoanPricingService: lowest borrower representative score across borrowers.
 * Accepts camelCase or PascalCase on the pricing loan payload; falls back to loan detail borrowers.
 */
export function resolveRepresentativeFico(loanDetail, pricingLoan = null) {
  const loan = pricingLoan || {};
  const fromPricingLoan = toFicoInt(loan.representativeFico ?? loan.RepresentativeFico);
  if (isValidRepresentativeFico(fromPricingLoan)) return fromPricingLoan;

  const fromLosLoan = toFicoInt(
    loanDetail?.loan?.lowestMedianCreditScore ?? loanDetail?.loan?.LowestMedianCreditScore,
  );
  if (isValidRepresentativeFico(fromLosLoan)) return fromLosLoan;

  const borrowerScores = [];
  for (const b of loanDetail?.borrowers || []) {
    const score = toFicoInt(b?.creditScore ?? b?.CreditScore);
    if (isValidRepresentativeFico(score)) borrowerScores.push(score);
  }
  if (borrowerScores.length === 0) return null;
  return Math.min(...borrowerScores);
}

/** Write normalized representativeFico onto a pricing request loan object (camelCase only). */
export function applyRepresentativeFicoToPricingLoan(loanDetail, pricingLoan) {
  if (!pricingLoan || typeof pricingLoan !== 'object') return null;
  const fico = resolveRepresentativeFico(loanDetail, pricingLoan);
  if (fico == null) {
    delete pricingLoan.representativeFico;
    delete pricingLoan.RepresentativeFico;
    return null;
  }
  pricingLoan.representativeFico = fico;
  delete pricingLoan.RepresentativeFico;
  return fico;
}

